// Demo data seeder — creates a small set of realistic-looking customers, parts,
// quotes, and an order so the M2 UI surfaces have something to render against.
//
// Idempotency: best-effort. Reads existing customers first and skips if any of
// the demo company names already exist. Safe to click the "Seed demo data"
// button twice without creating duplicates.
//
// Dev-only: meant to be triggered from src/components/DevToolsCard.tsx in a
// dev-mode-gated affordance. Don't ship a path to this in production builds.

import {
  createCustomer,
  createPart,
  createQuote,
  createOrder,
  listCustomers,
  type CreateCustomerInput,
  type CreatePartInput,
  type CreateQuoteInput,
  type CreateOrderInput,
} from './crm';

// =====================================================
// Demo customers — span every tier
// =====================================================

const DEMO_CUSTOMERS: CreateCustomerInput[] = [
  {
    code: 'ACME',
    companyName: 'Acme Manufacturing',
    contacts: [
      {
        id: 'c1',
        name: 'Sarah Lee',
        title: 'Purchasing Manager',
        email: 'sarah.lee@acme.com',
        phone: '(555) 432-9012',
        primary: true,
      },
      {
        id: 'c2',
        name: 'Mike Chen',
        title: 'Engineer',
        email: 'mike@acme.com',
        primary: false,
      },
    ],
    billingAddress: {
      line1: '4521 Industrial Way',
      city: 'Cleveland',
      state: 'OH',
      zip: '44114',
      country: 'USA',
    },
    terms: 'Net 30',
    taxExempt: false,
    rep: 'JMJ',
    tier: 'good',
    tierManualOverride: false,
    notes: [],
  },
  {
    code: 'BETA',
    companyName: 'Beta Industries',
    contacts: [
      {
        id: 'c1',
        name: 'Tom Reynolds',
        title: 'Owner',
        email: 'tom@beta-industries.com',
        phone: '(555) 778-1234',
        primary: true,
      },
    ],
    billingAddress: {
      line1: '12 Production Drive',
      city: 'Akron',
      state: 'OH',
      zip: '44308',
      country: 'USA',
    },
    terms: 'Net 30',
    taxExempt: false,
    rep: 'JMJ',
    tier: 'vip',
    tierManualOverride: true,
    tierLastReviewed: new Date().toISOString(),
    notes: [
      {
        ts: new Date(Date.now() - 30 * 86400000).toISOString(),
        user: 'justin',
        text: 'Repeat customer — always pays early. Treat as VIP.',
      },
    ],
  },
  {
    code: 'CCSP',
    companyName: 'Cleveland Custom Spares',
    contacts: [
      {
        id: 'c1',
        name: 'Linda Park',
        title: 'Buyer',
        email: 'lpark@cleveland-custom.com',
        phone: '(555) 902-5544',
        primary: true,
      },
    ],
    billingAddress: {
      line1: '8801 Brookline Road',
      city: 'Brunswick',
      state: 'OH',
      zip: '44212',
      country: 'USA',
    },
    terms: 'Net 30',
    taxExempt: false,
    rep: 'JMJ',
    tier: 'slow_pay',
    tierManualOverride: false,
    notes: [
      {
        ts: new Date(Date.now() - 60 * 86400000).toISOString(),
        user: 'justin',
        text: 'Two invoices ran past 60 days in Q1. Flag during quoting.',
      },
    ],
  },
  {
    code: 'DSMC',
    companyName: 'Dunbar Steel & Machinery Corp',
    contacts: [
      {
        id: 'c1',
        name: 'Greg Dunbar',
        title: 'President',
        email: 'greg@dunbarsteel.com',
        primary: true,
      },
    ],
    billingAddress: {
      line1: '1450 Foundry Lane',
      city: 'Youngstown',
      state: 'OH',
      zip: '44502',
      country: 'USA',
    },
    terms: 'COD',
    taxExempt: false,
    rep: 'JMJ',
    tier: 'bad',
    tierManualOverride: true,
    tierLastReviewed: new Date().toISOString(),
    notes: [
      {
        ts: new Date(Date.now() - 120 * 86400000).toISOString(),
        user: 'justin',
        text: 'Disputed three invoices last year. COD only going forward.',
      },
    ],
  },
  {
    code: 'EVRR',
    companyName: 'Everett Robotics',
    contacts: [
      {
        id: 'c1',
        name: 'Jamie Walsh',
        email: 'jwalsh@everett-robotics.com',
        phone: '(555) 311-7780',
        primary: true,
      },
    ],
    billingAddress: {
      line1: '350 Innovation Park',
      city: 'Solon',
      state: 'OH',
      zip: '44139',
      country: 'USA',
    },
    terms: 'Net 30',
    taxExempt: false,
    rep: 'JMJ',
    tier: 'new',
    tierManualOverride: false,
    notes: [],
  },
];

// =====================================================
// Demo parts — one per type
// =====================================================

const DEMO_PARTS: CreatePartInput[] = [
  {
    number: 'BRK-2245-A',
    description: 'Mounting bracket — Acme conveyor, 4-hole bolt pattern',
    type: 'machined',
    qtyTiers: [10, 50, 100],
    materialCost: {
      perPiece: [3.42, 3.18, 2.95],
      vendorId: '',
      vendorPartNum: '',
      vendorUrl: '',
      isComponent: false,
    },
    setupCost: 95,
    setupHrs: 1.0,
    machines: [],
    dxfFiles: [],
    outsideServices: [],
    tooling: [],
    attachedDrawings: [],
    notes: '6061-T6 aluminum, ±0.005 unless noted',
  },
  {
    number: 'PLT-1018-B',
    description: 'Cut blank — A36 plate, 8x12 mounting plate',
    type: 'cut_blank',
    qtyTiers: [25, 100, 250],
    materialCost: {
      perPiece: [4.5, 4.2, 3.85],
      vendorId: '',
      vendorPartNum: '',
      vendorUrl: '',
      isComponent: false,
    },
    setupCost: 0,
    machines: [],
    dxfFiles: [],
    outsideServices: [],
    tooling: [],
    attachedDrawings: [],
    notes: '1/4 A36 plate, laser cut from supplied DXF',
  },
  {
    number: 'WLD-FRM-001',
    description: 'Welded frame assembly — Beta Industries jig fixture',
    type: 'weldment',
    qtyTiers: [1, 5],
    materialCost: {
      perPiece: [185.0, 165.0],
      vendorId: '',
      vendorPartNum: '',
      vendorUrl: '',
      isComponent: false,
    },
    setupCost: 250,
    setupHrs: 3.5,
    machines: [],
    dxfFiles: [],
    outsideServices: [],
    tooling: [],
    weldment: {
      subParts: [],
      weldOps: [{ method: 'MIG', hrs: 4.5, electrodeType: 'ER70S-6', fillerCost: 8 }],
    },
    attachedDrawings: [],
    notes: 'AWS D1.1 — full pen welds throughout',
  },
  {
    number: 'HYB-MNT-007',
    description: 'Hybrid mount — laser-cut blank + drilled & tapped 1/4-20',
    type: 'hybrid',
    qtyTiers: [50, 200, 500],
    materialCost: {
      perPiece: [5.6, 5.1, 4.75],
      vendorId: '',
      vendorPartNum: '',
      vendorUrl: '',
      isComponent: false,
    },
    setupCost: 120,
    setupHrs: 1.5,
    machines: [],
    dxfFiles: [],
    outsideServices: [],
    tooling: [],
    attachedDrawings: [],
    notes: '11ga A36 — laser blanks then VMC drill/tap secondary',
  },
  {
    number: 'BLT-91290-A',
    description: 'McMaster 1/4-20 × 1.0 SHCS, 18-8 SS — pass-through markup',
    type: 'purchased',
    qtyTiers: [100, 500, 1000],
    materialCost: {
      perPiece: [0.42, 0.36, 0.31],
      vendorId: 'mcmaster',
      vendorPartNum: '91290A537',
      vendorUrl: 'https://www.mcmaster.com/91290A537/',
      isComponent: true,
    },
    purchasedSource: {
      vendor: 'mcmaster',
      sku: '91290A537',
      url: 'https://www.mcmaster.com/91290A537/',
      leadTime: '1 day',
      markupPct: 35,
    },
    attachedDrawings: [],
    notes: 'Stock catalog item — order ad-hoc, no inventory hold',
  },
];

// =====================================================
// Demo quotes — varied statuses
// =====================================================

function quoteFor(customerId: string, customerName: string, overrides: Partial<CreateQuoteInput> = {}): CreateQuoteInput {
  return {
    number: '',
    customerId,
    customerNameSnapshot: customerName,
    rfqRef: '',
    dueDate: new Date(Date.now() + 21 * 86400000).toISOString(),
    jobType: 'multi_part_production',
    partIds: [],
    partQuotedSpecs: {},
    qtyTiers: [10, 50, 100],
    status: 'draft',
    imported: false,
    totalsByTier: [0, 0, 0],
    marginsByTier: [0, 0, 0],
    marginWarning: false,
    approvalRequired: false,
    approvalStatus: 'na',
    revisions: [],
    followUp: {
      nextDueDate: '',
      stage: 'paused',
      draftsHistory: [],
      paused: true,
    },
    ...overrides,
  };
}

// =====================================================
// Main seeder
// =====================================================

export interface SeedResult {
  skipped: boolean;
  reason?: string;
  customersCreated: number;
  partsCreated: number;
  quotesCreated: number;
  ordersCreated: number;
}

/**
 * Idempotent seed. Returns counts of what got created. Skips entirely if any
 * of the demo customer codes already exist in Firebase.
 */
export async function seedDemoData(): Promise<SeedResult> {
  // Skip if demo data already loaded
  const existing = await listCustomers();
  const demoCodes = new Set(DEMO_CUSTOMERS.map((c) => c.code));
  const alreadyLoaded = existing.some((c) => demoCodes.has(c.code));
  if (alreadyLoaded) {
    return {
      skipped: true,
      reason: 'Demo data already loaded (at least one customer code matches).',
      customersCreated: 0,
      partsCreated: 0,
      quotesCreated: 0,
      ordersCreated: 0,
    };
  }

  // Customers — capture IDs so we can wire quotes to them
  const customerIds: Record<string, string> = {};
  for (const c of DEMO_CUSTOMERS) {
    const id = await createCustomer(c);
    customerIds[c.code] = id;
  }

  // Parts
  const partIds: string[] = [];
  for (const p of DEMO_PARTS) {
    const id = await createPart(p);
    partIds.push(id);
  }

  // Quotes — give each customer at least one quote in a meaningful state
  const acmeId = customerIds['ACME'];
  const betaId = customerIds['BETA'];
  const evrrId = customerIds['EVRR'];

  const quotesToCreate: CreateQuoteInput[] = [
    // Acme — active sent quote
    quoteFor(acmeId, 'Acme Manufacturing', {
      number: 'Q-1042',
      status: 'sent',
      partIds: [partIds[0]],
      partQuotedSpecs: {
        [partIds[0]]: {
          qtyTiers: [10, 50, 100],
          estimatedSetupHrs: 1.0,
          estimatedRunHrs: 0.25,
          materialCostSnapshot: 3.18,
          pricePerTier: [42.5, 32.75, 27.5],
        },
      },
      totalsByTier: [425, 1637.5, 2750],
      marginsByTier: [42, 38, 35],
      jobType: 'multi_part_production',
      sentAt: new Date(Date.now() - 8 * 86400000).toISOString(),
      followUp: {
        nextDueDate: new Date(Date.now() + 2 * 86400000).toISOString(),
        stage: 'day_7',
        draftsHistory: [],
        paused: false,
      },
    }),
    // Beta — VIP, won quote
    quoteFor(betaId, 'Beta Industries', {
      number: 'Q-1043',
      status: 'won',
      jobType: 'machine_build',
      partIds: [partIds[2]],
      partQuotedSpecs: {
        [partIds[2]]: {
          qtyTiers: [1, 5],
          estimatedSetupHrs: 3.5,
          estimatedRunHrs: 8.0,
          materialCostSnapshot: 185.0,
          pricePerTier: [1450.0, 1280.0],
        },
      },
      totalsByTier: [1450, 6400],
      marginsByTier: [45, 42],
      outcome: {
        decision: 'won',
        decidedAt: new Date(Date.now() - 14 * 86400000).toISOString(),
        reason: 'Selected on quality + lead time',
      },
    }),
    // Everett — new customer's first quote
    quoteFor(evrrId, 'Everett Robotics', {
      number: 'Q-1044',
      status: 'draft',
      jobType: 'prototype',
      partIds: [partIds[1]],
      partQuotedSpecs: {
        [partIds[1]]: {
          qtyTiers: [25, 100, 250],
          estimatedSetupHrs: 0,
          estimatedRunHrs: 0.15,
          materialCostSnapshot: 4.2,
          pricePerTier: [18.5, 14.75, 12.5],
        },
      },
      totalsByTier: [462.5, 1475, 3125],
      marginsByTier: [40, 35, 32],
    }),
  ];

  const quoteIds: Record<string, string> = {};
  for (const q of quotesToCreate) {
    const id = await createQuote(q);
    quoteIds[q.number] = id;
  }

  // One Order — from Beta's won quote
  const orderToCreate: CreateOrderInput = {
    number: 'O-2245',
    customerId: betaId,
    customerNameSnapshot: 'Beta Industries',
    fromQuoteId: quoteIds['Q-1043'],
    status: 'in_production',
    statusChangedAt: new Date(Date.now() - 7 * 86400000).toISOString(),
    totalSnapshot: 6400,
    qtyOrderedByTier: [5],
    poNumber: 'BETA-PO-2280',
    poReceivedAt: new Date(Date.now() - 12 * 86400000).toISOString(),
    promisedShipDate: new Date(Date.now() + 14 * 86400000).toISOString(),
    jobIds: [],
    invoiceIds: [],
    imported: false,
  };
  await createOrder(orderToCreate);

  return {
    skipped: false,
    customersCreated: DEMO_CUSTOMERS.length,
    partsCreated: DEMO_PARTS.length,
    quotesCreated: quotesToCreate.length,
    ordersCreated: 1,
  };
}
